import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';

export const getDailyBook = async (dispatch: Dispatch, queryParams: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/book/daily?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getMajorBook = async (dispatch: Dispatch, queryParams: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/book/major?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getMajorBookByIdentifier = async (
  dispatch: Dispatch,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/book/major/by-identifier?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFinancialStatementBook8Column = async (
  dispatch: Dispatch,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/book/8column?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAccountingEntries = async (
  dispatch: Dispatch,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/entries?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};
export const getAccountingEntryById = (
  dispatch: Dispatch,
  id?: string | number,
) =>
  useQuery({
    queryKey: ['accounting', 'entry', id],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/accounting/entries/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const createAccountingEntry = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounting', 'entries', 'create'],
    mutationFn: async (entry: any) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/accounting/entries`,
        dispatch,
        data: entry,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['accounting'],
      });
    },
  });
};

export const createAccountingEntriesBatch = async (
  dispatch: Dispatch,
  entry: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/accounting/entries/batch`,
    dispatch,
    data: entry,
  });
  if (response) return response.data;
};
export const createAccountingEntriesBatchFromTemplate = async (
  dispatch: Dispatch,
  entry: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/accounting/entries/batch/template`,
    dispatch,
    data: entry,
  });
  if (response) return response.data;
};

export const editAccountingEntry = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accounting', 'entries', 'edit'],
    mutationFn: async (entry: any) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/accounting/entries/${entry.id}`,
        dispatch,
        data: entry,
      });
      if (response) return response.data;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'entry', variables.id],
      });
    },
  });
};

export const getAccountingEntryTypes = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/entries/types`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAccountingNodes = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/entries/nodes`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteAccountingEntry = async (
  dispatch: Dispatch,
  entryId: any,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/accounting/entries/${entryId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAccountingPurchasesAndSales = async (
  dispatch: Dispatch,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/purchases-and-sales?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};
export const getCostCenters = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/cost-centers`,
    dispatch,
  });
  if (response) return response.data;
};

export const createCostCenter = async (
  dispatch: Dispatch,
  data: { name: string },
) => {
  const response = await requestHandler({
    method: 'post',
    url: 'internal/accounting/cost-centers',
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const getFinancialNodes = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/entries/nodes`,
    dispatch,
  });
  if (response) return response.data;
};

export const assignCostCenterAndFinancialNode = async (
  dispatch: Dispatch,
  assignments: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/accounting/documents/assign`,
    dispatch,
    data: assignments,
  });
  if (response) return response.data;
};

export const updateCostCenter = async (
  dispatch: Dispatch,
  id: string,
  data: { name: string },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/accounting/cost-centers/${id}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteCostCenter = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/accounting/cost-centers/${id}`,
    dispatch,
  });
  if (response) return response.data;
};
export const getAccountingEntryClasses = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: 'internal/accounting/entry-classes',
    dispatch,
  });
  if (response) return response.data;
};
export const getAllReceiptsFees = async (
  dispatch: Dispatch,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/receipt-fees?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createReceiptFee = async (dispatch: Dispatch, receiptFee: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/accounting/receipt-fees`,
    dispatch,
    data: receiptFee,
  });
  if (response) return response.data;
};

export const updateReceiptFee = async (
  dispatch: Dispatch,
  receiptFeeId: any,
  payload: any,
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/accounting/receipt-fees/${receiptFeeId}`,
    dispatch,
    data: payload,
  });
  if (response) return response.data;
};

export const getAccountingPeriods = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/accounting/periods`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateAccountingPeriod = async (
  dispatch: Dispatch,
  periodId: number,
  data: {
    status: string;
    closedAt?: Date;
    closedByUserId?: number;
    reopenedAt?: Date;
    reopenedById?: number;
  },
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/accounting/periods/${periodId}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};
